import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Registration } from '../../../types/registration';
import { Note } from '../../../types/camper';
import { addNote, updateNote } from '../../../utils/camperManagement';
import { useAuth } from '../../../context/AuthContext';

interface NotesCardProps {
  registration: Registration;
}

const NotesCard: React.FC<NotesCardProps> = ({ registration }) => {
  const { currentUser } = useAuth();
  const [primaryCamperNotes, setPrimaryCamperNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editIsPersistent, setEditIsPersistent] = useState(false);
  const [primaryCamperId, setPrimaryCamperId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteIsPersistent, setNewNoteIsPersistent] = useState(false);

  useEffect(() => {
    const fetchPrimaryCamperNotes = async () => {
      if (!registration?.registrationInfo?.email) {
        setLoading(false);
        return;
      }

      try {
        // Query primaryCampers collection by email
        const primaryCampersQuery = query(
          collection(db, 'primaryCampers'),
          where('email', '==', registration.registrationInfo.email)
        );
        const primaryCampersSnapshot = await getDocs(primaryCampersQuery);

        if (!primaryCampersSnapshot.empty) {
          const primaryCamperDoc = primaryCampersSnapshot.docs[0];
          const primaryCamperData = primaryCamperDoc.data();
          setPrimaryCamperId(primaryCamperDoc.id);
          
          const notes = primaryCamperData.notes || [];
          
          // Convert Firestore timestamps to Date objects
          const convertedNotes = notes.map((note: any) => ({
            ...note,
            createdDate: note.createdDate?.toDate ? note.createdDate.toDate() : new Date(note.createdDate),
            lastModifiedDate: note.lastModifiedDate?.toDate ? note.lastModifiedDate.toDate() : new Date(note.lastModifiedDate)
          }));
          
          setPrimaryCamperNotes(convertedNotes);
        }
      } catch (error) {
        console.error('Error fetching primary camper notes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrimaryCamperNotes();
  }, [registration]);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  };

  const startEditing = (note: Note) => {
    setEditingNoteId(note.id);
    setEditContent(note.content);
    setEditIsPersistent(note.isPersistent);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditContent('');
    setEditIsPersistent(false);
  };

  const handleSaveNote = async (noteId: string) => {
    if (!editContent.trim() || !primaryCamperId) return;

    setSaving(true);
    try {
      await updateNote(primaryCamperId, noteId, editContent, editIsPersistent);
      
      // Update local state
      setPrimaryCamperNotes(prevNotes =>
        prevNotes.map(note =>
          note.id === noteId
            ? { ...note, content: editContent, isPersistent: editIsPersistent, lastModifiedDate: new Date() }
            : note
        )
      );
      
      cancelEditing();
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Failed to update note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim() || !primaryCamperId || !currentUser) return;

    setSaving(true);
    try {
      const newNote = await addNote(primaryCamperId, newNoteContent, currentUser.uid, newNoteIsPersistent);
      
      // Add to local state
      setPrimaryCamperNotes(prevNotes => [...prevNotes, {
        ...newNote,
        createdDate: newNote.createdDate instanceof Date ? newNote.createdDate : new Date(newNote.createdDate),
        lastModifiedDate: newNote.lastModifiedDate instanceof Date ? newNote.lastModifiedDate : new Date(newNote.lastModifiedDate)
      }]);
      
      // Reset form
      setNewNoteContent('');
      setNewNoteIsPersistent(false);
      setIsAddingNote(false);
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const cancelAddNote = () => {
    setIsAddingNote(false);
    setNewNoteContent('');
    setNewNoteIsPersistent(false);
  };

  const accommodationNotes = (registration?.accommodation as any)?.notes;
  const hasAccommodationNotes = accommodationNotes && accommodationNotes.trim().length > 0;
  const hasPrimaryCamperNotes = primaryCamperNotes.length > 0;

  return (
    <div className="card mb-3">
      <div className="card-body p-3">
        <h6 className="card-title mb-3" style={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
          Notes
        </h6>

        {loading ? (
          <p className="text-muted">Loading notes...</p>
        ) : (
          <>
            {/* Accommodation Notes Section */}
            <div className="mb-3">
              <h6 style={{ fontWeight: 'bold', color: '#555', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                Registration/Accommodation Notes:
              </h6>
              {hasAccommodationNotes ? (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#f8f9fa',
                  borderLeft: '3px solid #007bff',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}>
                  {accommodationNotes}
                </div>
              ) : (
                <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
                  No accommodation notes for this registration
                </p>
              )}
            </div>

            {/* Primary Camper Notes Section */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h6 style={{ fontWeight: 'bold', color: '#555', fontSize: '0.9rem', margin: 0 }}>
                  Primary Camper Notes:
                </h6>
                {!isAddingNote && (
                  <button
                    onClick={() => setIsAddingNote(true)}
                    style={{
                      padding: '0.3rem 0.6rem',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}
                  >
                    + Add Note
                  </button>
                )}
              </div>

              {/* Add Note Form */}
              {isAddingNote && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#fff',
                  borderLeft: '3px solid #28a745',
                  borderRadius: '4px',
                  marginBottom: '0.75rem'
                }}>
                  <textarea
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    placeholder="Enter note content..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      marginBottom: '0.5rem',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                      <input
                        type="checkbox"
                        checked={newNoteIsPersistent}
                        onChange={(e) => setNewNoteIsPersistent(e.target.checked)}
                      />
                      <span>Persistent Note (stays across years)</span>
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={handleAddNote}
                      disabled={!newNoteContent.trim() || saving}
                      style={{
                        padding: '0.4rem 0.8rem',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: !newNoteContent.trim() || saving ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        opacity: !newNoteContent.trim() || saving ? 0.6 : 1
                      }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={cancelAddNote}
                      disabled={saving}
                      style={{
                        padding: '0.4rem 0.8rem',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {hasPrimaryCamperNotes ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {primaryCamperNotes.map((note) => (
                    <div
                      key={note.id}
                      style={{
                        padding: '0.75rem',
                        backgroundColor: editingNoteId === note.id ? '#fff' : (note.isPersistent ? '#fff3cd' : '#f8f9fa'),
                        borderLeft: editingNoteId === note.id ? '3px solid #007bff' : (note.isPersistent ? '3px solid #ffc107' : '3px solid #6c757d'),
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                      }}
                    >
                      {editingNoteId === note.id ? (
                        // Edit mode
                        <div>
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={3}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              marginBottom: '0.5rem',
                              fontFamily: 'inherit'
                            }}
                          />
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '0.5rem'
                          }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                              <input
                                type="checkbox"
                                checked={editIsPersistent}
                                onChange={(e) => setEditIsPersistent(e.target.checked)}
                              />
                              <span>Persistent Note (stays across years)</span>
                            </label>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => handleSaveNote(note.id)}
                              disabled={!editContent.trim() || saving}
                              style={{
                                padding: '0.4rem 0.8rem',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: !editContent.trim() || saving ? 'not-allowed' : 'pointer',
                                fontSize: '0.85rem',
                                opacity: !editContent.trim() || saving ? 0.6 : 1
                              }}
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={cancelEditing}
                              disabled={saving}
                              style={{
                                padding: '0.4rem 0.8rem',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                fontSize: '0.85rem'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <>
                          <div style={{ marginBottom: '0.5rem' }}>
                            {note.content}
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            fontSize: '0.75rem',
                            color: '#6c757d',
                            marginBottom: '0.5rem'
                          }}>
                            <span>
                              {formatDate(note.createdDate)}
                              {note.lastModifiedDate > note.createdDate && 
                                ` (edited ${formatDate(note.lastModifiedDate)})`
                              }
                            </span>
                            {note.isPersistent && (
                              <span style={{
                                backgroundColor: '#ffc107',
                                color: '#000',
                                padding: '0.15rem 0.5rem',
                                borderRadius: '3px',
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                              }}>
                                PERSISTENT
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => startEditing(note)}
                            style={{
                              padding: '0.3rem 0.6rem',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            Edit
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
                  No primary camper notes
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NotesCard;

